import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Tv, PlayCircle, Star, Search, X, Edit2, Trash2, AlertTriangle } from "lucide-react";
import "./Series.css";

export default function Series() {
    const [seriesList, setSeriesList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    // TMDB Search
    const [tmdbQuery, setTmdbQuery] = useState("");
    const [tmdbResults, setTmdbResults] = useState([]);
    const [tmdbLoading, setTmdbLoading] = useState(false);

    // Form
    const initialForm = {
        title: "",
        genre: "",
        year: "",
        status: "Em Exibição",
        watchStatus: "Quero Assistir",
        seasons: 1,
        totalEpisodes: 0,
        watchedEpisodes: 0,
        posterUrl: "",
        rating: 0,
        notes: "",
        tmdbId: null,
    };
    const [formData, setFormData] = useState(initialForm);

    const fetchSeries = async () => {
        try {
            const res = await axios.get("http://localhost:5000/api/series");
            setSeriesList(res.data || []);
        } catch (err) {
            console.error("Erro ao carregar séries:", err);
        }
    };

    useEffect(() => {
        fetchSeries();
    }, []);

    // Estatísticas dos cards do topo
    const assistindoList = seriesList.filter(s => s.watchStatus === "Assistindo");
    const queroAssistirList = seriesList.filter(s => s.watchStatus === "Quero Assistir");
    const finalizadasList = seriesList.filter(s => s.watchStatus === "Finalizada" || s.watchStatus === "Concluída");

    // TMDB Search
    const handleSearchTMDB = async (e) => {
        e?.preventDefault();
        if (!tmdbQuery.trim()) return;

        setTmdbLoading(true);
        try {
            const res = await axios.get("http://localhost:5000/api/series/tmdb/search", {
                params: { query: tmdbQuery },
            });
            setTmdbResults(res.data);
        } catch (err) {
            console.error("Erro ao buscar no TMDB:", err);
        } finally {
            setTmdbLoading(false);
        }
    };

    const handleSelectTmdb = async (item) => {
        try {
            const res = await axios.get(`http://localhost:5000/api/series/tmdb/details/${item.tmdbId}`);
            const d = res.data;
            setFormData(prev => ({
                ...prev,
                title: d.title || item.title,
                genre: d.genre || "",
                year: d.year || item.year || "",
                status: d.status || "Em Exibição",
                seasons: d.seasons || 1,
                totalEpisodes: d.totalEpisodes || 0,
                posterUrl: d.posterUrl || item.posterUrl || "",
                rating: d.rating || item.rating || 0,
                tmdbId: d.tmdbId,
            }));
        } catch (err) {
            setFormData(prev => ({
                ...prev,
                title: item.title,
                year: item.year || "",
                posterUrl: item.posterUrl || "",
                rating: item.rating || 0,
                tmdbId: item.tmdbId,
            }));
        }
        setTmdbResults([]);
        setTmdbQuery("");
    };

    const handleOpenCreate = () => {
        setIsEditing(false);
        setCurrentId(null);
        setFormData(initialForm);
        setTmdbResults([]);
        setTmdbQuery("");
        setShowModal(true);
    };

    const handleOpenEdit = (serie) => {
        setIsEditing(true);
        setCurrentId(serie.id);
        setFormData({
            title: serie.title || "",
            genre: serie.genre || "",
            year: serie.year || "",
            status: serie.status || "Em Exibição",
            watchStatus: serie.watchStatus || "Quero Assistir",
            seasons: serie.seasons || 1,
            totalEpisodes: serie.totalEpisodes || 0,
            watchedEpisodes: serie.watchedEpisodes || 0,
            posterUrl: serie.posterUrl || "",
            rating: serie.rating || 0,
            notes: serie.notes || "",
            tmdbId: serie.tmdbId || null,
        });
        setTmdbResults([]);
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await axios.put(`http://localhost:5000/api/series/${currentId}`, formData);
            } else {
                await axios.post("http://localhost:5000/api/series", formData);
            }
            setShowModal(false);
            fetchSeries();
        } catch (err) {
            console.error("Erro ao salvar série:", err);
        }
    };

    const requestDelete = (id, title) => {
        setItemToDelete({ id, title });
    };

    // Executa a exclusão de fato ao clicar em "Sim, Remover"
    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await axios.delete(`http://localhost:5000/api/series/${itemToDelete.id}`);
            fetchSeries();
        } catch (err) {
            console.error("Erro ao excluir série:", err);
        } finally {
            setItemToDelete(null);
        }
    };

    // Renderiza uma seção de categoria
    const renderSection = (title, icon, list) => (
        <div className="series-category-group">
            <h2 className="series-section-title">
                {icon} {title} <span>({list.length})</span>
            </h2>

            {list.length === 0 ? (
                <div className="empty-category-notice">
                    <p>Nenhuma série aqui.</p>
                </div>
            ) : (
                <div className="series-grid">
                    {list.map((serie) => (
                        <div key={serie.id} className="serie-card">
                            <div
                                className="serie-poster"
                                style={{
                                    backgroundImage: serie.posterUrl
                                        ? `url(${serie.posterUrl})`
                                        : "none",
                                }}
                            >
                                <span className={`status-badge ${serie.watchStatus.toLowerCase().replace(/\s+/g, "")}`}>
                                    {serie.watchStatus}
                                </span>
                            </div>

                            <div className="serie-info">
                                <div className="serie-title-row">
                                    <h3>{serie.title}</h3>
                                </div>
                                <span className="serie-meta">
                                    {serie.year} • {serie.genre || "Gênero não informado"}
                                </span>

                                <div className="progress-section">
                                    <div className="progress-text">
                                        <span>Episódios</span>
                                        <span>{serie.watchedEpisodes} / {serie.totalEpisodes}</span>
                                    </div>
                                    <div className="progress-bar-bg">
                                        <div
                                            className="progress-bar-fill"
                                            style={{
                                                width: `${serie.totalEpisodes > 0
                                                    ? Math.min(100, (serie.watchedEpisodes / serie.totalEpisodes) * 100)
                                                    : 0}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="serie-actions">
                                    <button onClick={() => handleOpenEdit(serie)} title="Editar">
                                        <Edit2 size={16} />
                                    </button>
                                    <button className="del" onClick={() => requestDelete(serie.id, serie.title)} title="Excluir">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="series-container">
            {/* Header */}
            <div className="series-header">
                <div>
                    <h1>Minhas Séries</h1>
                    <p>Acompanhe seus episódios e avaliações.</p>
                </div>
                <button className="btn-add-serie" onClick={handleOpenCreate}>
                    <Plus size={18} /> Nova Série
                </button>
            </div>

            {/* Stats Dashboard */}
            <div className="series-stats-grid">
                <div className="stat-card">
                    <div className="icon-box blue">
                        <Tv size={24} />
                    </div>
                    <div>
                        <h3>Total de Séries</h3>
                        <span className="stat-number">{seriesList.length}</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="icon-box green">
                        <PlayCircle size={24} />
                    </div>
                    <div>
                        <h3>Assistindo</h3>
                        <span className="stat-number">{assistindoList.length}</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="icon-box yellow">
                        <Star size={24} />
                    </div>
                    <div>
                        <h3>Finalizadas</h3>
                        <span className="stat-number">{finalizadasList.length}</span>
                    </div>
                </div>
            </div>

            {/* Seções por Categoria */}
            {renderSection("Assistindo", "🎬", assistindoList)}
            {renderSection("Quero Assistir", "📺", queroAssistirList)}
            {renderSection("Finalizadas", "⭐", finalizadasList)}

            {/* MODAL DE FORMULÁRIO */}
            {showModal && (
                <div className="series-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="series-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="series-modal-header">
                            <div>
                                <h2>{isEditing ? "Editar Série" : "Adicionar Série"}</h2>
                                <p>Preencha os dados ou busque no TMDB</p>
                            </div>
                            <button
                                type="button"
                                className="btn-close-clean"
                                onClick={() => setShowModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {!isEditing && (
                            <div className="series-search-tmdb-wrapper">
                                <div className="tmdb-search-input-row">
                                    <input
                                        type="text"
                                        placeholder="Buscar série no TMDB..."
                                        value={tmdbQuery}
                                        onChange={(e) => setTmdbQuery(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleSearchTMDB(e)}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSearchTMDB}
                                        disabled={tmdbLoading}
                                        className="btn-tmdb-search"
                                    >
                                        <Search size={16} />
                                        <span>{tmdbLoading ? "..." : "Buscar"}</span>
                                    </button>
                                </div>

                                {tmdbResults.length > 0 && (
                                    <div className="tmdb-results-flyout">
                                        {tmdbResults.map((item) => (
                                            <div
                                                key={item.tmdbId}
                                                className="tmdb-result-item"
                                                onClick={() => handleSelectTmdb(item)}
                                            >
                                                {item.posterUrl ? (
                                                    <img src={item.posterUrl} alt={item.title} />
                                                ) : (
                                                    <div className="tmdb-empty-thumb">N/A</div>
                                                )}
                                                <div className="tmdb-item-text">
                                                    <strong>{item.title}</strong>
                                                    <small>{item.year || "N/A"}</small>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSave} className="series-form-body">
                            <div className="form-group full-width">
                                <label>Título da Série</label>
                                <input
                                    type="text"
                                    placeholder="Ex: Breaking Bad"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-row-2">
                                <div className="form-group">
                                    <label>Gênero</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Drama, Crime"
                                        value={formData.genre}
                                        onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Ano de Lançamento</label>
                                    <input
                                        type="number"
                                        placeholder="Ex: 2008"
                                        value={formData.year}
                                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-row-2">
                                <div className="form-group">
                                    <label>Status da Série</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="Em Exibição">Em Exibição</option>
                                        <option value="Finalizada">Finalizada</option>
                                        <option value="Cancelada">Cancelada</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Meu Progresso</label>
                                    <select
                                        value={formData.watchStatus}
                                        onChange={(e) => setFormData({ ...formData, watchStatus: e.target.value })}
                                    >
                                        <option value="Assistindo">Assistindo</option>
                                        <option value="Quero Assistir">Quero Assistir</option>
                                        <option value="Finalizada">Finalizada</option>
                                        <option value="Abandonada">Abandonada</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row-3">
                                <div className="form-group">
                                    <label>Temporadas</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.seasons}
                                        onChange={(e) => setFormData({ ...formData, seasons: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Total Episódios</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.totalEpisodes}
                                        onChange={(e) => setFormData({ ...formData, totalEpisodes: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Eps Assistidos</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.watchedEpisodes}
                                        onChange={(e) => setFormData({ ...formData, watchedEpisodes: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Sua Avaliação</label>
                                <div className="stars-picker">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                            key={star}
                                            size={20}
                                            className={star <= formData.rating ? "star-fill" : "star-empty"}
                                            onClick={() =>
                                                setFormData({
                                                    ...formData,
                                                    rating: star === formData.rating ? 0 : star,
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label>URL do Pôster</label>
                                <input
                                    type="text"
                                    placeholder="https://image.tmdb.org/..."
                                    value={formData.posterUrl}
                                    onChange={(e) => setFormData({ ...formData, posterUrl: e.target.value })}
                                />
                            </div>

                            <div className="form-group full-width">
                                <label>Observações</label>
                                <textarea
                                    rows="3"
                                    placeholder="Anotações sobre a série..."
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </div>

                            <div className="series-modal-footer">
                                <button
                                    type="button"
                                    className="btn-series-cancel"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-series-confirm">
                                    Salvar Série
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- MODAL DE CONFIRMAÇÃO DE EXCLUSÃO --- */}
            {itemToDelete && (
                <div className="series-confirm-overlay" onClick={() => setItemToDelete(null)}>
                    <div className="series-modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="series-modal-icon">
                            <AlertTriangle size={44} />
                        </div>
                        <h3>Remover Série?</h3>
                        <p>
                            Deseja remover <strong>"{itemToDelete.title}"</strong> da sua lista?
                        </p>
                        <div className="series-modal-actions">
                            <button
                                className="series-btn-cancel"
                                onClick={() => setItemToDelete(null)}
                            >
                                Cancelar
                            </button>
                            <button className="series-btn-confirm" onClick={confirmDelete}>
                                Sim, Remover
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}